/**
 * RecurringUploadDialog — «העלאה קבועה» (ADR-0037 phase 4, Turbo's RecurringUploadDialog): a file that
 * arrives every month is remembered by its shape (its column headers) together with the choices made
 * for it now — type, column roles, ledger, defaults and file rules. The next upload of a file with the
 * same headers starts from them, and the due date moves on. (Turbo's e-mail reminders and inbox need
 * inbound mail, which this system does not have — the file is uploaded here as usual.)
 */
import { useEffect, useMemo, useState } from "react";
import { Banner, Button, Dialog, Field, IconButton, Segmented, Tag, TextInput, ckConfirm, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import {
  aiDataEntryApi,
  type ColumnGuessDto,
  type DataEntrySessionDto,
  type ImportType,
  type RecurringDto,
  type RecurringFrequency,
} from "../../../api/aiDataEntryApi";
import { DeSection } from "./DataEntryWorkspace";
import { IMPORT_TYPE_OPTIONS, shortDate } from "./dataEntryModel";

export const FREQUENCY_OPTIONS: Array<{ value: RecurringFrequency; label: string }> = [
  { value: "weekly", label: "שבועי" },
  { value: "monthly", label: "חודשי" },
  { value: "bi_monthly", label: "דו-חודשי" },
  { value: "quarterly", label: "רבעוני" },
  { value: "yearly", label: "שנתי" },
  { value: "custom", label: "אחר" },
];

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** the next due day (same rules as the server's addPeriod) */
export function addPeriod(day: string, frequency: RecurringFrequency, customDays?: number | string | null): string {
  const d = new Date(`${day}T12:00:00`);
  if (frequency === "weekly") d.setDate(d.getDate() + 7);
  else if (frequency === "bi_monthly") d.setMonth(d.getMonth() + 2);
  else if (frequency === "quarterly") d.setMonth(d.getMonth() + 3);
  else if (frequency === "yearly") d.setFullYear(d.getFullYear() + 1);
  else if (frequency === "custom") d.setDate(d.getDate() + (Number(customDays) || 30));
  else d.setMonth(d.getMonth() + 1);
  return ymd(d);
}

const MONTHS =
  /(^|\s)(ינואר|פברואר|מרץ|מרס|אפריל|מאי|יוני|יולי|אוגוסט|ספטמבר|אוקטובר|נובמבר|דצמבר|תשרי|חשון|חשוון|כסלו|טבת|שבט|אדר|ניסן|אייר|סיון|סיוון|תמוז|אב|אלול|january|february|march|april|may|june|july|august|september|october|november|december)(?=\s|$)/gi;

/** "דף חשבון מזרחי ספטמבר 09-2026 (2).xlsx" → "דף חשבון מזרחי" — the name of the series, not of this copy */
export function suggestRecurringName(fileName: string, fallback: string): string {
  const base = String(fileName || "")
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/[\d_\-./()]+/g, " ")
    .replace(MONTHS, " ")
    .replace(/\s+/g, " ")
    .trim();
  return base.length >= 3 ? base.slice(0, 80) : fallback || "קובץ קבוע";
}

const errorText = (e: unknown, fallback: string) => (e as { message?: string })?.message || fallback;

export default function RecurringUploadDialog({
  open,
  onClose,
  session,
  importType,
  columns,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  session: DataEntrySessionDto;
  importType: ImportType;
  columns: ColumnGuessDto[];
  onSaved: (r: RecurringDto) => void;
}) {
  const [name, setName] = useState("");
  const [frequency, setFrequency] = useState<RecurringFrequency>("monthly");
  const [customDays, setCustomDays] = useState("30");
  const [nextDueDate, setNextDueDate] = useState("");
  const [dueTouched, setDueTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<RecurringDto | null>(null);
  const typeLabel = IMPORT_TYPE_OPTIONS.find((o) => o.value === importType)?.label ?? "";

  useEffect(() => {
    if (!open) return;
    setName(session.recurring?.name ?? suggestRecurringName(session.fileName, session.insights?.profile.label ?? typeLabel));
    setFrequency("monthly");
    setCustomDays("30");
    setNextDueDate(addPeriod(ymd(new Date()), "monthly"));
    setDueTouched(false);
    setError(null);
    setSaved(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && !dueTouched) setNextDueDate(addPeriod(ymd(new Date()), frequency, customDays));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequency, customDays]);

  const facts = useMemo(
    () =>
      [
        `${session.totalRows.toLocaleString("he-IL")} שורות`,
        `${session.headers.length} עמודות`,
        typeLabel,
        session.ledger ? `קופה: ${session.ledger.name}` : null,
      ].filter(Boolean),
    [session, typeLabel],
  );
  const invalid = !name.trim() || !/^\d{4}-\d{2}-\d{2}$/.test(nextDueDate) || (frequency === "custom" && !(Number(customDays) >= 1));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await aiDataEntryApi.saveRecurring(session.id, {
        name: name.trim(),
        frequency,
        customIntervalDays: frequency === "custom" ? Number(customDays) : null,
        nextDueDate,
        importType,
        columns: columns.map((c) => ({ header: c.header, role: c.role })),
      });
      setSaved(r);
      onSaved(r);
    } catch (e) {
      setError(errorText(e, "השמירה נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  if (saved) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        title="ההעלאה הקבועה נשמרה"
        icon="check"
        size="md"
        footer={
          <Button variant="primary" onClick={onClose}>
            סיום
          </Button>
        }
      >
        <div className="de-rec">
          <p>
            <b>«{saved.name}»</b> · {saved.frequencyLabel} · הפעם הבאה: <b>{shortDate(saved.nextDueDate)}</b>
          </p>
          <ul className="de-rec__done">
            <li>בפעם הבאה שתעלו קובץ עם אותן עמודות, הסוכן יזהה אותו ויתחיל מההגדרות של היום — סוג, עמודות, קופה, ברירות מחדל וכללים.</li>
            <li>המועד הבא מתעדכן לבד כשהקובץ מגיע. העלאות שהמועד שלהן עבר מסומנות במסך הפתיחה של ההזנה החכמה.</li>
          </ul>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? () => undefined : onClose}
      title="העלאה קבועה"
      icon="repeat"
      size="md"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            ביטול
          </Button>
          <Button variant="primary" icon="check" onClick={() => void save()} disabled={busy || invalid}>
            {busy ? "שומר…" : "שמירה"}
          </Button>
        </>
      }
    >
      <div className="de-rec">
        <p className="de-muted">
          הסוכן יזכור את מבנה הקובץ ואת הבחירות שעשיתם עכשיו (סוג, עמודות, קופה, ברירות מחדל וכללים), ויזהה אותו בכל פעם שיגיע · {facts.join(" · ")}
        </p>
        {session.recurring && !session.recurring.applied ? <Banner tone="info">הקובץ כבר שמור כהעלאה קבועה «{session.recurring.name}» — שמירה מעדכנת אותה.</Banner> : null}
        <Field label="שם ההעלאה" hint="למשל «דף בנק מזרחי» או «דוח תשלומי אשראי»">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} maxLength={160} autoFocus />
        </Field>
        <Field label="כל כמה זמן מגיע הקובץ?">
          <Segmented ariaLabel="תדירות" value={frequency} onChange={(v) => setFrequency(v as RecurringFrequency)} items={FREQUENCY_OPTIONS} />
        </Field>
        <div className="de-rec__row">
          {frequency === "custom" ? (
            <Field label="כל כמה ימים">
              <TextInput type="number" min={1} max={366} value={customDays} onChange={(e) => setCustomDays(e.target.value)} />
            </Field>
          ) : null}
          <Field label="המועד הבא" hint="מתי הקובץ הבא אמור להגיע">
            <TextInput
              type="date"
              value={nextDueDate}
              onChange={(e) => {
                setDueTouched(true);
                setNextDueDate(e.target.value);
              }}
            />
          </Field>
        </div>
        {error ? <Banner tone="danger">{error}</Banner> : null}
      </div>
    </Dialog>
  );
}

/** the side column of the upload step: the recurring uploads and when each is due */
export function RecurringSection({ items, onRemove }: { items: RecurringDto[]; onRemove: (r: RecurringDto) => void }) {
  const toast = useCkToast();
  if (!items.length) return null;
  const overdue = items.filter((r) => r.overdue).length;
  const remove = async (r: RecurringDto) => {
    const ok = await ckConfirm({ title: "להסיר את ההעלאה הקבועה?", message: `«${r.name}» לא תזוהה יותר בהעלאה. מה שכבר נרשם לא משתנה.`, confirmLabel: "הסרה" });
    if (!ok) return;
    try {
      await aiDataEntryApi.deleteRecurring(r.id);
      onRemove(r);
    } catch (e) {
      toast.error(errorText(e, "ההסרה נכשלה"));
    }
  };
  return (
    <DeSection title="העלאות קבועות" end={overdue ? <Tag tone="warning">{overdue} באיחור</Tag> : <Tag>{items.length}</Tag>}>
      <ul className="de-reclist">
        {items.map((r) => (
          <li key={r.id} className={r.overdue ? "is-overdue" : ""}>
            <span className="de-reclist__t">
              <b>{r.name}</b>
              <small className="de-muted">
                {r.frequencyLabel} · {r.overdue ? "היה אמור להגיע" : "הבא"}: {shortDate(r.nextDueDate)}
              </small>
            </span>
            <IconButton size="sm" icon="trash" label="הסרה" onClick={() => void remove(r)} />
          </li>
        ))}
      </ul>
      <div className="ck-sec__muted">מעלים את הקובץ כרגיל — הסוכן מזהה אותו לפי העמודות ומתחיל מההגדרות שנשמרו.</div>
    </DeSection>
  );
}
